'use client'

import { useRouter } from 'next/navigation'
import { useTopLoader } from 'nextjs-toploader'
import querystring, { encode, ParsedUrlQuery } from 'querystring'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { Params, stripUnwantedRouteParams } from '../utils/querystring'
import { buildQueryState } from '../utils/querystate'

type ProviderProps = { query: ParsedUrlQuery; children: React.ReactNode }
type QueryState = ReturnType<typeof buildQueryState>

const QueryParamsContext = React.createContext({
  getActiveFilterValue: (_key: string) => ['temp'],
  queryState: {} as QueryState,
  queryString: '',
  setQueryState: (_newParams: Params, _scroll?: boolean) => {},
})

export const QueryStateProvider: FunctionComponent<ProviderProps> = ({ query, children }) => {
  const queryString = encode(query)

  const queryState = useMemo(() => buildQueryState(query), [queryString])

  const getActiveFilterValue = useCallback(
    (key: string) => {
      const filter = queryState.activeFilters.find(f => f.name === key)

      return filter ? filter.values.filter(Boolean) : []
    },
    [queryState.activeFilters]
  )

  const router = useRouter()
  const topLoader = useTopLoader()

  const setQueryState = useCallback(
    (newParams: Params, scroll = true) => {
      if (typeof window === 'undefined') {
        return
      }

      const oldParams = window.location.search
        ? querystring.parse(window.location.search.slice(1))
        : {}

      const query = stripUnwantedRouteParams({ ...oldParams, ...newParams })

      Object.entries(query).forEach(([key, value]) => {
        if (!value) {
          delete query[key]
        }
      })

      const sortedQueryParams = Object.fromEntries(
        Object.entries(query).sort((a, b) => (a[0] > b[0] ? 1 : -1))
      )

      const pathname = window.location.pathname || ''
      const path = pathname.split('?')[0].split('#')[0]
      const newQuerystring = querystring.stringify(sortedQueryParams).replace(/%2C/g, ',')
      const newUrl = newQuerystring ? `${path}?${newQuerystring}` : path

      topLoader.start()
      router.replace(newUrl, { scroll })
    },
    [router, topLoader]
  )

  return (
    <QueryParamsContext.Provider
      value={{ queryState, setQueryState, queryString, getActiveFilterValue }}>
      {children}
    </QueryParamsContext.Provider>
  )
}

export const useQueryState = () => {
  const context = React.useContext(QueryParamsContext)

  if (!context || !context.queryState || !context.setQueryState) {
    throw new Error('useQueryState must be used within a QueryStateProvider')
  }

  return context
}
